import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

export interface UserCourseSettings {
  id: string;
  userId: string;
  courseId: string;
  coverImageUrl: string | null;
  iconUrl: string | null;
  iconEmoji: string | null;
  iconName: string | null;
  iconColor: string | null;
}

function mapSettings(row: any): UserCourseSettings {
  return {
    id: row.id,
    userId: row.user_id,
    courseId: row.course_id,
    coverImageUrl: row.cover_image_url,
    iconUrl: row.icon_url,
    iconEmoji: row.icon_emoji,
    iconName: row.icon_name ?? null,
    iconColor: row.icon_color ?? null,
  };
}

export function useUserCourseSettings(courseId: string | undefined) {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["user-course-settings", userId, courseId],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("user_course_settings") as any)
        .select("*")
        .eq("user_id", userId!)
        .eq("course_id", courseId!)
        .maybeSingle();
      if (error) throw error;
      return data ? mapSettings(data) : null;
    },
    enabled: !!userId && !!courseId,
  });

  const upsert = async (
    fields: Partial<
      Pick<
        UserCourseSettings,
        "coverImageUrl" | "iconUrl" | "iconEmoji" | "iconName" | "iconColor"
      >
    >
  ) => {
    if (!userId || !courseId) return;

    const payload: Record<string, any> = {
      user_id: userId,
      course_id: courseId,
    };
    if (fields.coverImageUrl !== undefined) payload.cover_image_url = fields.coverImageUrl;
    if (fields.iconUrl !== undefined) payload.icon_url = fields.iconUrl;
    if (fields.iconEmoji !== undefined) payload.icon_emoji = fields.iconEmoji;
    if (fields.iconName !== undefined) payload.icon_name = fields.iconName;
    if (fields.iconColor !== undefined) payload.icon_color = fields.iconColor;

    const { error } = await (supabase
      .from("user_course_settings") as any)
      .upsert(payload, { onConflict: "user_id,course_id" });

    if (error) {
      toast({ title: "Failed to save settings", description: error.message, variant: "destructive" });
      throw error;
    }

    queryClient.invalidateQueries({ queryKey: ["user-course-settings", userId, courseId] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-data", userId] });
  };

  const uploadCover = async (file: File) => {
    if (!userId || !courseId) return;
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `${userId}/${courseId}/cover.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("course-assets")
      .upload(path, file, { upsert: true });
    if (uploadError) {
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      throw uploadError;
    }

    const { data } = supabase.storage.from("course-assets").getPublicUrl(path);
    // Append cache-bust to avoid stale CDN
    const url = `${data.publicUrl}?t=${Date.now()}`;
    await upsert({ coverImageUrl: url });
    return url;
  };

  const uploadIcon = async (file: File) => {
    if (!userId || !courseId) return;
    const ext = file.name.split(".").pop() ?? "png";
    const path = `${userId}/${courseId}/icon.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("course-assets")
      .upload(path, file, { upsert: true });
    if (uploadError) {
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      throw uploadError;
    }

    const { data } = supabase.storage.from("course-assets").getPublicUrl(path);
    const url = `${data.publicUrl}?t=${Date.now()}`;
    await upsert({ iconUrl: url, iconEmoji: null, iconName: null, iconColor: null });
    return url;
  };

  const removeCover = async () => {
    await upsert({ coverImageUrl: null });
  };

  const removeIcon = async () => {
    await upsert({ iconUrl: null, iconEmoji: null, iconName: null, iconColor: null });
  };

  const setLucideIcon = async (name: string | null, color: string) => {
    await upsert({
      iconName: name,
      iconColor: color,
      iconUrl: null,
      iconEmoji: null,
    });
  };

  return {
    settings: query.data ?? null,
    isLoading: query.isLoading,
    upsert,
    uploadCover,
    uploadIcon,
    removeCover,
    removeIcon,
    setLucideIcon,
  };
}
